/**
 * `editorStore` + `useEditor` (W1-09 state-store; contract src/state/types.ts, ARCHITECTURE.md §5.5).
 *
 * Non-document UI/session state in a zustand vanilla store: workspace, active line and layer, focused and pinned
 * columns, cross-surface hover, pinned lanes and "also show" lines, tool and placed note kind, selection, snap,
 * timeline view, preview UI, preview-only filters, placement state, loop, metronome and rate. Not undoable (the
 * selection rides on every transaction through the SelectionBridge, see state/connect.ts).
 *
 * Mirrors: `level`, `layout` and `returnOnStop` mirror the user settings (settings.general.level,
 * settings.editing.layout, settings.audio.spaceStops === 'return'), which are the single source of truth. The setters
 * only change the mirror; `connectSettingsMirror` (wired by W1-16) keeps both sides in sync in both directions.
 *
 * Setters normalise their input (snap divisor 1..1920, zoom and lane bounds, opacities 0..1, rate, layers, Phira
 * player note scale and aspect), skip no-op updates (hover at mouse-move rate, or a filter toggle that ends where it
 * started, does not notify) and keep unchanged parts by identity (sets and maps with the same contents included).
 * Per-project state is reset by `openProject` (restoring a persisted view) and `closeProject`.
 */

import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Unsubscribe } from '../core/util/types';
import type { LineId, NoteKind, ProjectId } from '../core/model/types';
import type { PhiraPlayerSettings } from '../core/engine/types';
import { Beats } from '../core/time/beat';
import { useExternalSelector } from './react';
import { Sel } from './selection';
import type {
  ColumnRef,
  EditorStore,
  EditorStoreApi,
  EditorStoreState,
  ExperienceLevel,
  HoverTarget,
  LayoutPreset,
  PersistedViewState,
  PlacementState,
  PreviewUiState,
  ToolId,
  UseEditor,
} from './types';
import {
  DEFAULT_SNAP,
  DEFAULT_TIMELINE,
  EMPTY_FILTERS,
  normalizeFilters,
  normalizeSnap,
  normalizeTimeline,
  sameColumn,
  uniqueColumns,
  uniqueIds,
  ViewState,
} from './viewState';

// ---------------------------------------------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------------------------------------------

/** Phira's player defaults (preview-only; DESIGN.md §4.8 "Phira settings"). */
export const DEFAULT_PLAYER: PhiraPlayerSettings = Object.freeze({
  aspectOverride: null,
  noteScale: 1,
  doubleHint: true,
  particles: true,
  aggressiveCull: true,
  strictAutoplayHitColor: true,
  msaa: false,
  apFcIndicator: true,
});

export const DEFAULT_PREVIEW: PreviewUiState = Object.freeze({
  mode: 'edit',
  showHud: true,
  lineLabels: true,
  safeAreas: 'none',
  hiddenGhosts: true,
  gizmo: true,
  autoKey: false,
  sampleMode: false,
  showInvisibleLines: true,
  theater: false,
  player: DEFAULT_PLAYER,
  timeOverride: null,
});

/** Phira player note scale and aspect override bounds (the same as settings.preview.player, app/settings). */
export const PLAYER_NOTE_SCALE_MIN = 0.1;
export const PLAYER_NOTE_SCALE_MAX = 10;
export const PLAYER_ASPECT_MIN = 0.1;
export const PLAYER_ASPECT_MAX = 10;

/** Playback rate bounds. */
export const RATE_MIN = 0.05;
export const RATE_MAX = 4;
/** Highest additive layer index the editor addresses (imports may carry more than RPE's 4). */
export const ACTIVE_LAYER_MAX = 255;

const NOTE_TOOLS: Readonly<Partial<Record<ToolId, NoteKind>>> = Object.freeze({
  tap: 'tap',
  drag: 'drag',
  flick: 'flick',
  hold: 'hold',
});

/** The initial editor state (no project open). */
export function initialEditorState(): EditorStoreState {
  return {
    projectId: null,
    workspace: 'chart',
    level: 'guided',
    layout: 'compose',
    activeLine: null,
    activeLayer: 0,
    focusedColumn: null,
    pinnedColumns: [],
    hover: null,
    pinnedLanes: [],
    alsoShow: [],
    tool: 'select',
    placeKind: 'tap',
    selection: Sel.EMPTY,
    snap: DEFAULT_SNAP,
    timeline: DEFAULT_TIMELINE,
    preview: DEFAULT_PREVIEW,
    filters: EMPTY_FILTERS,
    placement: null,
    loop: null,
    metronome: false,
    rate: 1,
    returnOnStop: false,
    tempoPolicy: 'keepBeats',
  };
}

/** Per-project fields reset when a project opens or closes (session preferences such as tool and preview stay). */
function projectDefaults(): Pick<
  EditorStoreState,
  | 'workspace'
  | 'activeLine'
  | 'activeLayer'
  | 'focusedColumn'
  | 'pinnedColumns'
  | 'hover'
  | 'pinnedLanes'
  | 'alsoShow'
  | 'selection'
  | 'snap'
  | 'timeline'
  | 'filters'
  | 'placement'
  | 'loop'
  | 'tempoPolicy'
> {
  return {
    workspace: 'chart',
    activeLine: null,
    activeLayer: 0,
    focusedColumn: null,
    pinnedColumns: [],
    hover: null,
    pinnedLanes: [],
    alsoShow: [],
    selection: Sel.EMPTY,
    snap: DEFAULT_SNAP,
    timeline: DEFAULT_TIMELINE,
    filters: EMPTY_FILTERS,
    placement: null,
    loop: null,
    tempoPolicy: 'keepBeats',
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Equality helpers (no-op updates do not notify)
// ---------------------------------------------------------------------------------------------------------------

function sameHover(a: HoverTarget | null, b: HoverTarget | null): boolean {
  if (a === b) return true;
  if (a === null || b === null || a.kind !== b.kind) return false;
  if (a.kind === 'note') return b.kind === 'note' && a.note === b.note;
  return b.kind === 'lanePoint' && a.line === b.line && a.x === b.x && a.beat === b.beat;
}

function sameList<T>(a: readonly T[], b: readonly T[], eq: (x: T, y: T) => boolean = Object.is): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!eq(a[i] as T, b[i] as T)) return false;
  return true;
}

function shallowSame<T extends object>(a: T, b: T): boolean {
  if (a === b) return true;
  const ka = Object.keys(a) as (keyof T)[];
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) if (!Object.is(a[k], b[k])) return false;
  return true;
}

function samePlayer(a: PhiraPlayerSettings, b: PhiraPlayerSettings): boolean {
  return shallowSame(a, b);
}

/** `v` within lo..hi; -0 becomes 0 (no-op detection compares with Object.is). */
function clampNum(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v === 0 ? 0 : v;
}

/** A finite number clamped to lo..hi; `base` for undefined and non-finite input. */
function boundedOr(v: number | undefined, base: number, lo: number, hi: number): number {
  return v === undefined || !Number.isFinite(v) ? base : clampNum(v, lo, hi);
}

/** Aspect override: null (the chart's own aspect) or a finite ratio in bounds; `base` for undefined or non-finite. */
function playerAspect(v: number | null | undefined, base: number | null): number | null {
  if (v === undefined) return base;
  if (v === null) return null;
  return Number.isFinite(v) ? clampNum(v, PLAYER_ASPECT_MIN, PLAYER_ASPECT_MAX) : base;
}

/**
 * `base` with the defined fields of `p` applied: note scale and aspect override clamped to the player bounds
 * (non-finite values ignored; aspect null = the chart's own). Returns `base` itself when nothing changed.
 */
export function normalizePlayer(
  p: Partial<PhiraPlayerSettings>,
  base: PhiraPlayerSettings = DEFAULT_PLAYER,
): PhiraPlayerSettings {
  const apFcIndicator = p.apFcIndicator ?? base.apFcIndicator;
  const next: PhiraPlayerSettings = {
    aspectOverride: playerAspect(p.aspectOverride, base.aspectOverride),
    noteScale: boundedOr(p.noteScale, base.noteScale, PLAYER_NOTE_SCALE_MIN, PLAYER_NOTE_SCALE_MAX),
    doubleHint: p.doubleHint ?? base.doubleHint,
    particles: p.particles ?? base.particles,
    aggressiveCull: p.aggressiveCull ?? base.aggressiveCull,
    strictAutoplayHitColor: p.strictAutoplayHitColor ?? base.strictAutoplayHitColor,
    msaa: p.msaa ?? base.msaa,
    // Optional (Phira default on; the engine treats absent as on): only set when either side has it.
    ...(apFcIndicator === undefined ? {} : { apFcIndicator }),
  };
  return samePlayer(base, next) ? base : next;
}

// ---------------------------------------------------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------------------------------------------------

/** Additional actions of this implementation (beyond the contract's EditorActions). */
export interface EditorActionsExt {
  /** Resets per-project state when the project closes (Library). */
  closeProject(): void;
  /** Activates `line` and focuses one of its columns (palette "Focus column...", clicking a pinned column). */
  focusColumn(c: ColumnRef): void;
}

export type EditorStoreExt = EditorStore & EditorActionsExt;

export interface EditorStoreApiExt extends EditorStoreApi {
  getState(): EditorStoreExt;
  subscribe(listener: (s: EditorStoreExt, prev: EditorStoreExt) => void): Unsubscribe;
}

let playheadProvider: () => number = () => 0;

/** Where `snapshotView` reads the playhead beat (the transport; wired by W1-16). */
export function setPlayheadProvider(fn: () => number): void {
  playheadProvider = fn;
}

/** The active-line change and the state that follows it (focused column, placements tied to the old line). */
function activeLinePatch(s: EditorStoreState, id: LineId | null): Partial<EditorStoreState> | null {
  if (s.activeLine === id) return null;
  const patch: { -readonly [K in keyof EditorStoreState]?: EditorStoreState[K] } = { activeLine: id };
  const fc = s.focusedColumn;
  // The focused column follows the active line (same property and layer), unless it is a pinned column.
  if (fc !== null && fc.line === s.activeLine && !s.pinnedColumns.some((c) => sameColumn(c, fc))) {
    patch.focusedColumn = id === null ? null : { line: id, prop: fc.prop, layer: fc.layer };
  }
  // Hold and segment placements belong to the line they started on.
  const p = s.placement;
  if (p !== null && p.kind !== 'paste' && p.line !== id) patch.placement = null;
  return patch;
}

/** A new editor store (the app uses the `editorStore` singleton; tests and tools may create their own). */
export function createEditorStore(initial: Partial<EditorStoreState> = {}): StoreApi<EditorStoreExt> {
  return createStore<EditorStoreExt>()((set, get) => {
    /** Applies a patch unless every field is unchanged by identity. */
    const patch = (p: Partial<EditorStoreState>): void => {
      const s = get();
      for (const k of Object.keys(p) as (keyof EditorStoreState)[]) {
        if (!Object.is(s[k], p[k])) {
          set(p);
          return;
        }
      }
    };

    return {
      ...initialEditorState(),
      ...initial,

      setWorkspace: (w) => {
        patch({ workspace: w });
      },
      setLevel: (l: ExperienceLevel) => {
        patch({ level: l });
      },
      setLayout: (p: LayoutPreset) => {
        patch({ layout: p });
      },
      setActiveLine: (id) => {
        const p = activeLinePatch(get(), id);
        if (p !== null) set(p);
      },
      setActiveLayer: (layer) => {
        if (!Number.isFinite(layer)) return;
        patch({ activeLayer: clampNum(Math.trunc(layer), 0, ACTIVE_LAYER_MAX) });
      },
      setFocusedColumn: (c) => {
        if (!sameColumn(get().focusedColumn, c)) set({ focusedColumn: c });
      },
      focusColumn: (c) => {
        const s = get();
        const lineChange =
          s.activeLine === c.line || s.pinnedColumns.some((p) => sameColumn(p, c)) ? null : activeLinePatch(s, c.line);
        set({ ...(lineChange ?? {}), focusedColumn: c });
      },
      setPinnedColumns: (cols) => {
        const next = uniqueColumns(cols);
        if (!sameList(get().pinnedColumns, next, sameColumn)) set({ pinnedColumns: next });
      },
      setHover: (h) => {
        if (!sameHover(get().hover, h)) set({ hover: h });
      },
      setTool: (t) => {
        const kind = NOTE_TOOLS[t];
        patch(kind === undefined ? { tool: t } : { tool: t, placeKind: kind });
      },
      setPlaceKind: (k) => {
        patch({ placeKind: k });
      },
      select: (next, mode = 'replace') => {
        const cur = get().selection;
        const sel = Sel.combine(cur, next, mode);
        if (sel !== cur) set({ selection: sel });
      },
      clearSelection: () => {
        patch({ selection: Sel.EMPTY });
      },
      setSnap: (p) => {
        const cur = get().snap;
        const next = normalizeSnap(p, cur);
        if (!shallowSame(cur, next)) set({ snap: next });
      },
      setTimeline: (p) => {
        const cur = get().timeline;
        const next = normalizeTimeline(p, cur);
        if (!shallowSame(cur, next)) set({ timeline: next });
      },
      setPreview: (p) => {
        const cur = get().preview;
        const next: PreviewUiState = {
          ...cur,
          ...p,
          player: p.player === undefined ? cur.player : normalizePlayer(p.player, cur.player),
        };
        if (!shallowSame(cur, next)) set({ preview: next });
      },
      setFilters: (p) => {
        const cur = get().filters;
        const next = normalizeFilters(p, cur);
        if (!shallowSame(cur, next)) set({ filters: next });
      },
      setPlacement: (p: PlacementState | null) => {
        patch({ placement: p });
      },
      setLoop: (l) => {
        if (l === null) {
          patch({ loop: null });
          return;
        }
        const c = Beats.cmp(l.start, l.end);
        if (c === 0) {
          patch({ loop: null });
          return;
        }
        const next = c < 0 ? l : { start: l.end, end: l.start };
        const cur = get().loop;
        if (cur !== null && Beats.eq(cur.start, next.start) && Beats.eq(cur.end, next.end)) return;
        set({ loop: next });
      },
      setPinnedLanes: (lines) => {
        const next = uniqueIds(lines);
        if (!sameList(get().pinnedLanes, next)) set({ pinnedLanes: next });
      },
      setAlsoShow: (lines) => {
        const next = uniqueIds(lines);
        if (!sameList(get().alsoShow, next)) set({ alsoShow: next });
      },
      setMetronome: (on) => {
        patch({ metronome: on });
      },
      setRate: (r) => {
        if (!Number.isFinite(r) || r <= 0) return;
        patch({ rate: clampNum(r, RATE_MIN, RATE_MAX) });
      },
      setReturnOnStop: (on) => {
        patch({ returnOnStop: on });
      },
      setTempoPolicy: (p) => {
        patch({ tempoPolicy: p === 'keepTime' ? 'keepTime' : 'keepBeats' });
      },
      openProject: (id: ProjectId, view: PersistedViewState | null) => {
        const restored = view === null ? {} : ViewState.restore(view);
        set({
          ...projectDefaults(),
          projectId: id,
          ...restored,
          preview: { ...get().preview, theater: false, timeOverride: null },
        });
      },
      closeProject: () => {
        set({
          ...projectDefaults(),
          projectId: null,
          preview: { ...get().preview, theater: false, timeOverride: null },
        });
      },
      snapshotView: () => ViewState.snapshot(get(), playheadProvider()),
    };
  });
}

const store = createEditorStore();

/** THE editor store of the app. */
export const editorStore: EditorStoreApiExt = {
  getState: () => store.getState(),
  subscribe: (listener) => store.subscribe(listener),
};

const subscribeEditor = (cb: () => void): Unsubscribe =>
  store.subscribe(() => {
    cb();
  });
const getEditor = (): EditorStoreExt => store.getState();

/** Selects from the editor store; re-renders only when the selection is not `equal` (default Object.is). */
export const useEditor: UseEditor = (selector, equal) =>
  useExternalSelector(store, subscribeEditor, getEditor, selector, equal);

/** Effective preview mode: Theater (and test play) always show the clean render. */
export function effectivePreviewMode(p: PreviewUiState): PreviewUiState['mode'] {
  return p.theater ? 'clean' : p.mode;
}

// ---------------------------------------------------------------------------------------------------------------
// Settings mirror
// ---------------------------------------------------------------------------------------------------------------

/** The mirrored settings, as the settings service exposes them (spaceStops 'return' = returnOnStop). */
export interface MirroredSettings {
  readonly level: ExperienceLevel;
  readonly layout: LayoutPreset;
  readonly returnOnStop: boolean;
}

/** Adapter over the settings service (state/ cannot import app/). */
export interface SettingsMirrorPort {
  read(): MirroredSettings;
  write(p: Partial<MirroredSettings>): void;
  subscribe(cb: () => void): Unsubscribe;
}

/**
 * Keeps `level`, `layout` and `returnOnStop` in sync with the settings in both directions. The settings win on
 * connect; afterwards a setter on either side updates the other (no echo loops).
 */
export function connectSettingsMirror(port: SettingsMirrorPort, target: EditorStoreApi = editorStore): Unsubscribe {
  let syncing = false;
  const pull = (): void => {
    if (syncing) return;
    syncing = true;
    try {
      const s = port.read();
      const e = target.getState();
      if (e.level !== s.level) e.setLevel(s.level);
      if (e.layout !== s.layout) e.setLayout(s.layout);
      if (e.returnOnStop !== s.returnOnStop) e.setReturnOnStop(s.returnOnStop);
    } finally {
      syncing = false;
    }
  };
  const push = (e: EditorStore): void => {
    if (syncing) return;
    const s = port.read();
    const diff: { -readonly [K in keyof MirroredSettings]?: MirroredSettings[K] } = {};
    if (e.level !== s.level) diff.level = e.level;
    if (e.layout !== s.layout) diff.layout = e.layout;
    if (e.returnOnStop !== s.returnOnStop) diff.returnOnStop = e.returnOnStop;
    if (Object.keys(diff).length === 0) return;
    syncing = true;
    try {
      port.write(diff);
    } finally {
      syncing = false;
    }
  };
  pull();
  const offSettings = port.subscribe(pull);
  const offEditor = target.subscribe((s, prev) => {
    if (s.level !== prev.level || s.layout !== prev.layout || s.returnOnStop !== prev.returnOnStop) push(s);
  });
  let active = true;
  return () => {
    if (!active) return;
    active = false;
    offSettings();
    offEditor();
  };
}
